import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from '../../../environments/environment';

type TelemetryItemWithUri = {
  baseData?: {
    uri?: unknown;
  } & Record<string, unknown>;
};

@Injectable({ providedIn: 'root' })
export class TelemetryService {
  private readonly client: ApplicationInsights | null;
  private readonly exceptionDedupeWindowMs = 60_000;
  private readonly exceptionDedupe = new Map<string, number>();

  constructor() {
    const rawConnectionString =
      (environment as { applicationInsightsConnectionString?: unknown }).applicationInsightsConnectionString;
    const connectionString = typeof rawConnectionString === 'string' ? rawConnectionString.trim() : '';

    if (!connectionString) {
      this.client = null;
      return;
    }

    const appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: false,
      },
    });

    appInsights.loadAppInsights();

    appInsights.addTelemetryInitializer((item: unknown) => {
      this.sanitizeTelemetryUrl(item as TelemetryItemWithUri);
      return true;
    });

    this.client = appInsights;
  }

  trackEvent(name: string, properties?: Record<string, string>): void {
    if (!this.client) {
      return;
    }

    this.client.trackEvent({
      name,
      properties,
    });
  }

  trackException(error: unknown, properties?: Record<string, string>): void {
    if (!this.client) {
      return;
    }

    const { exception, extractedProperties } = this.normalizeException(error);
    const mergedProperties = {
      ...extractedProperties,
      ...(properties ?? {}),
    };

    const normalizedProperties = this.normalizeTelemetryProperties(mergedProperties);
    const dedupeKey = this.buildExceptionDedupeKey(exception, normalizedProperties);

    if (!this.shouldTrackException(dedupeKey)) {
      return;
    }

    this.client.trackException({
      exception,
      properties: normalizedProperties,
    });
  }

  private shouldTrackException(dedupeKey: string): boolean {
    const now = Date.now();
    const lastTrackedAt = this.exceptionDedupe.get(dedupeKey);

    if (lastTrackedAt !== undefined && now - lastTrackedAt < this.exceptionDedupeWindowMs) {
      return false;
    }

    this.exceptionDedupe.set(dedupeKey, now);

    // Keep map bounded by pruning old keys opportunistically.
    if (this.exceptionDedupe.size > 200) {
      for (const [key, timestamp] of this.exceptionDedupe.entries()) {
        if (now - timestamp >= this.exceptionDedupeWindowMs) {
          this.exceptionDedupe.delete(key);
        }
      }
    }

    return true;
  }

  private buildExceptionDedupeKey(exception: Error, properties: Record<string, string>): string {
    const source = properties['source'] ?? '';
    const status = properties['status'] ?? '';
    const method = properties['method'] ?? '';
    const url = properties['url'] ?? '';
    return `${source}|${status}|${method}|${url}|${exception.name}|${exception.message}`;
  }

  private normalizeTelemetryProperties(properties: Record<string, string>): Record<string, string> {
    const normalized = { ...properties };

    if (normalized['url']) {
      normalized['url'] = this.sanitizeUrlString(normalized['url']);
    }

    return normalized;
  }

  private normalizeException(error: unknown): {
    exception: Error;
    extractedProperties: Record<string, string>;
  } {
    if (error instanceof Error) {
      return {
        exception: error,
        extractedProperties: {},
      };
    }

    if (typeof error === 'string') {
      return {
        exception: new Error(error),
        extractedProperties: { nonErrorExceptionType: 'string' },
      };
    }

    if (typeof error === 'object' && error !== null) {
      const objectError = error as Record<string, unknown>;
      const message = this.resolveObjectErrorMessage(objectError);

      return {
        exception: new Error(message),
        extractedProperties: {
          nonErrorExceptionType: this.resolveObjectType(objectError),
          errorCode: this.toStringValue(objectError['errorCode']),
          errorMessage: this.toStringValue(objectError['errorMessage']),
        },
      };
    }

    return {
      exception: new Error(String(error)),
      extractedProperties: {
        nonErrorExceptionType: typeof error,
      },
    };
  }

  private resolveObjectErrorMessage(error: Record<string, unknown>): string {
    const messageCandidates = [error['errorMessage'], error['message'], error['error_description'], error['error'], error['errorCode']];

    for (const candidate of messageCandidates) {
      const stringValue = this.toStringValue(candidate);
      if (stringValue) {
        return stringValue;
      }
    }

    return 'Non-Error exception object';
  }

  private resolveObjectType(error: Record<string, unknown>): string {
    const ctor =
      typeof error.constructor === 'function' && 'name' in error.constructor ? (error.constructor.name as string) : 'Object';

    return ctor || 'Object';
  }

  private toStringValue(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    return '';
  }

  private sanitizeTelemetryUrl(item: TelemetryItemWithUri): void {
    if (!item.baseData || !('uri' in item.baseData)) {
      return;
    }

    const uri = item.baseData.uri;

    if (typeof uri !== 'string') {
      return;
    }

    try {
      item.baseData.uri = this.sanitizeUrlString(uri);
    } catch {
      // Ignore invalid telemetry URLs.
    }
  }

  private sanitizeUrlString(value: string): string {
    try {
      const url = new URL(value);
      return `${url.origin}${url.pathname}`;
    } catch {
      return value.split('?')[0];
    }
  }
}


